/**
 * A manually-scrollable list of suggested questions, shown only in the Ask panel's empty
 * state.
 *
 * Replaces the panel's old blob decoration (VRG-227) — the empty state's job is to teach a
 * first-time user what to ask, not to hold a mood for a few seconds before the first
 * keystroke erases it.
 *
 * ── History: three designs, in order ────────────────────────────────────────────────────
 *   v1 — ~7 questions auto-scrolling continuously, faded top/bottom edges. Rejected:
 *        "distracting" — ambient motion pulled the eye toward tracking movement instead
 *        of reading.
 *   v2/v3 — 3 at a time behind pager arrows (first up/down, then left/right with a
 *        wrapping slice so every page stayed full). Committed, then reverted: it added a
 *        second navigation model (arrows) on top of the one the panel already has
 *        (scrolling), for no benefit over just letting the user scroll.
 *   v4 (this version) — a vertical list, 3–5 rows visible, faded top/bottom, scrolled by
 *        the user's own wheel/trackpad/touch. No arrows, no paging, no motion of any kind
 *        that the user didn't directly cause.
 *
 * The pager's machinery — arrows, page index, direction state, the slide `@keyframes`, the
 * wrapping-slice helper (`getAskSuggestedQuestionsPage` / `askSuggestedQuestionsPageCount`
 * in `suggestedQuestions.ts`), and the page-position `aria-live` announcer — existed only to
 * serve paging. With scrolling there is no "page" to be on, so none of it has a job; all of
 * it was deleted at the source rather than left disabled. Two mechanisms for the same job
 * (arrows AND scroll) would have been worse than either alone.
 *
 * ── The v1 fade objection no longer applies ─────────────────────────────────────────────
 * v1's rejection reasoning (kept here because it was almost repeated by mistake): "a faded
 * edge invites WAITING in a surface whose only success condition is a CLICK." That was true
 * of v1 specifically because v1's list was moving on its own — waiting was rational when the
 * content underneath the fade was actively changing. This list is static; nothing arrives on
 * its own. On a static list a faded edge is the standard "there's more, scroll" affordance,
 * which is exactly what was asked for here. The two designs only look similar (both have a
 * fade); the thing the fade was warning about is gone.
 *
 * ── Fade: static, not scroll-position-reactive, and why ─────────────────────────────────
 * Ideally the top fade would only show once scrolled away from the top, and the bottom only
 * while there's more below. That needs either a JS scroll listener driving opacity (ruled
 * out: the Claude preview pane fires ZERO `requestAnimationFrame`/`ResizeObserver` callbacks,
 * so anything gated on them can never be verified there — see the sibling `AskPanel` /
 * `AskComposer` docblocks) or a pure-CSS scroll-driven `animation-timeline: scroll()` mask,
 * which is real but young enough (and un-verifiable in the same preview pane, for the same
 * reason — no rAF, and no way to synthesize a real scroll gesture there) that shipping it
 * blind wasn't worth the risk. So both edges fade unconditionally via a static `mask-image`
 * on the scroll container itself — not an overlay `Box`, specifically so there is no
 * click-swallowing surface sitting on top of the questions (a masked element stays fully
 * hit-testable; only its paint is faded). This is a perfectly good outcome on its own terms
 * and is literally what was asked for ("fade top and bottom"); scroll-reactivity is a
 * possible future refinement, not a gap in this implementation.
 *
 * ── Order: shuffled once per mount, not a randomised scroll offset ──────────────────────
 * The old pager randomised its STARTING PAGE once per mount so the same three questions
 * weren't always first. A scrolling list has no page to start on, and randomising the
 * scroll offset instead would look like the panel loaded mid-list (broken, not varied).
 * Variety is achieved the same way it always should have been: the corpus order itself is
 * shuffled once, lazily, on first render of this mount — then held stable for the life of
 * that view, so a question read and scrolled past can still be found again by scrolling
 * back. The list always starts scrolled to the top.
 *
 * ── Nested scrolling ─────────────────────────────────────────────────────────────────────
 * This list sits inside the panel's own scrollable empty-state column. `overscrollBehavior:
 * 'contain'` on the list stops a wheel gesture that reaches either end of the question list
 * from "scroll-chaining" into the panel behind it — reaching the bottom of the questions
 * doesn't yank the whole panel down.
 *
 * ── A click fills the composer; it does not send (VRG-227) ─────────────────────────────
 * A suggestion is a starting point, not a submission — several of these are close to what
 * someone wants but not exact ("Which product gets scanned the most?" when they meant a
 * specific product), so sending on click would spend a query on a question they never quite
 * asked. Clicking calls `prefill`, a channel distinct from the ⌘K palette's `ask` (which
 * still sends immediately) — see both docblocks in `useAskStore`.
 */
import React, { useState } from 'react'

import { Box, Typography, useTheme } from '@mui/material'

import { useAskStore } from 'ask'
import { ASK_SUGGESTED_QUESTIONS } from 'ask/suggestedQuestions'

/**
 * ONE line at body3's 18px line-height (`packages/ui/theme/typography.ts`) plus the row's
 * own `py: 1.5` (6px top + 6px bottom on the platform's 4px spacing base, §29) — 30px, the
 * height a single-line question occupies NATURALLY.
 *
 * Rows are NOT pinned to this. An earlier version set `minHeight` to a two-line box (48px)
 * so that `VISIBLE_ROWS * ROW_HEIGHT_PX` would mean exactly "N rows fit" — but almost every
 * question in the corpus is one line, so in practice that padded every row by 18px of empty
 * space and the list read as loose and over-spaced. Intrinsic height restores the original
 * rhythm; a rare two-line question is simply taller, which is correct.
 *
 * The constant survives only as the unit the container height and fade depth are expressed
 * in. That makes the container height APPROXIMATE ("about this many rows") rather than
 * exact, which is the right trade: the list scrolls, so a part-row at the bottom edge is a
 * feature — it is the thing that says "there is more below".
 */
const BODY3_LINE_HEIGHT_PX = 18
const ROW_VERTICAL_PADDING_PX = 6
const ROW_HEIGHT_PX = BODY3_LINE_HEIGHT_PX + ROW_VERTICAL_PADDING_PX * 2

/**
 * Deliberately a half-row over six, not a round seven.
 *
 * A container sized to a whole number of rows makes the last visible row end flush with the
 * container's edge, which looks like the end of the list — the one impression this surface
 * must not give, since the whole point is that 35 questions are reachable. Cutting a row in
 * half guarantees a partial row is always visible at the bottom.
 *
 * Was 5.5 (165px). Raised alongside the fade depths below — a longer fade eats into the
 * readable area from both ends, so growing the fade without growing the container would have
 * left barely one row at full opacity in the middle. 6.5 rows keeps ~2.5 fully legible once
 * the deeper fades below are subtracted.
 */
const VISIBLE_ROWS = 6.5
const LIST_MAX_HEIGHT_PX = Math.round(ROW_HEIGHT_PX * VISIBLE_ROWS)

/**
 * LONGER than a row — the ramp has to outlast the row it is fading, or it reads as a cut.
 *
 * This has now been wrong in two directions, which is the useful part:
 *
 *   20px  — thinner than a row, so it caught only a sliver of text and was effectively
 *           invisible against the panel. Read as a hard clip.
 *   30px  — exactly one row. Better, but the bottom row still terminated at full opacity a
 *           few pixels before the container edge, so the eye read the boundary as a cut
 *           with a slight blur rather than as text receding.
 *
 * The mistake both times was matching the fade to the row. A gradient that finishes in the
 * same distance the row occupies spends its whole ramp on that one row and still hits
 * `transparent` abruptly at the edge. Running it over ~1.75 rows (top) / ~2.5 rows (bottom)
 * means the LAST FULL row is already dimming well before the cut row begins, so opacity falls
 * across two rows and there is no single line where the list visibly stops.
 *
 * Raised again from 1/1.75 rows to 1.5/2.5 rows — still asymmetric, still the same ratio in
 * spirit (bottom noticeably longer than top), just a visibly deeper fade at both edges, per
 * direct feedback that the previous depth read as too subtle. The container (`VISIBLE_ROWS`
 * above) grew from 5.5 to 6.5 rows to absorb the extra fade without eating further into the
 * legible middle.
 *
 * ── The two edges are NOT symmetric, and should not be ──────────────────────────────────
 * The bottom edge ALWAYS has content past it (80 questions in a 6.5-row window), so its job
 * is to make text recede rather than stop — it gets the long ramp.
 *
 * The top edge does not. The list always opens scrolled to the top, so at rest there is
 * nothing above the first row, and a long top fade would dim a fully-available question to
 * advertise content that does not exist. Its fade only becomes meaningful once the user has
 * scrolled, so it gets the shorter ramp: enough to read clearly as "more above" once it is
 * true, cheap enough not to hurt when it isn't.
 *
 * A scroll-reactive mask would let both be exactly right, and is ruled out above for
 * reasons that have nothing to do with this — so the asymmetry is the honest approximation
 * of a fade that cannot see where it is.
 */
const FADE_TOP_PX = Math.round(ROW_HEIGHT_PX * 1.5)
const FADE_BOTTOM_PX = Math.round(ROW_HEIGHT_PX * 2.5)

/** Fisher–Yates. Local and tiny — not worth a shared util for one caller. */
const shuffle = <T,>(items: readonly T[]): T[] => {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

export const AskSuggestedQuestions = () => {
  const theme = useTheme()
  const prefill = useAskStore(s => s.prefill)

  // Shuffled ONCE per mount (a fresh panel-open / new chat), then held stable — see the
  // header docblock. Lazy initializer so the shuffle only ever runs on the first render of
  // this particular mount, not on every re-render.
  const [questions] = useState(() => shuffle(ASK_SUGGESTED_QUESTIONS))

  return (
    <Box
      role="group"
      aria-label="Suggested questions"
      sx={{
        width: '100%',
        maxWidth: 420,
        maxHeight: LIST_MAX_HEIGHT_PX,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        /**
         * No scrollbar. The FADE is the affordance here, and a scrollbar beside it is a
         * second control saying the same thing in a 360px-wide panel where horizontal
         * space is scarce.
         *
         * It is also the wrong instrument for this content. A scrollbar answers "where am
         * I in a finite sequence" — useful in a document, meaningless in a shuffled bag of
         * suggestions nobody reads in order or needs to return to by position. And if this
         * list is ever made to LOOP, a scrollbar becomes actively false: it would render a
         * thumb at a position in a sequence with no end, that never reaches the bottom.
         *
         * Hiding it does not disable scrolling — wheel, trackpad, touch and keyboard all
         * still work; only the painted indicator is suppressed.
         */
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        // Faded top + bottom edges via a mask on the scroll container itself — see the
        // header docblock on why this is a mask (stays clickable) rather than an overlay
        // (would swallow clicks), and why it's static rather than scroll-reactive.
        WebkitMaskImage: `linear-gradient(to bottom, transparent 0, black ${FADE_TOP_PX}px, black calc(100% - ${FADE_BOTTOM_PX}px), transparent 100%)`,
        maskImage: `linear-gradient(to bottom, transparent 0, black ${FADE_TOP_PX}px, black calc(100% - ${FADE_BOTTOM_PX}px), transparent 100%)`,
      }}
    >
      {questions.map((question, i) => (
        <Box
          key={i}
          component="button"
          type="button"
          onClick={() => prefill(question)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            // No `minHeight` — see ROW_HEIGHT_PX's docblock. Pinning every row to a
            // two-line box padded the one-line majority with 18px of dead space.
            // Centered, matching the empty state above it — this is a centered mark +
            // label, not a left-aligned list.
            textAlign: 'center',
            font: 'inherit',
            appearance: 'none',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            color: theme.palette.text.secondary,
            transition: 'color 0.12s ease, background-color 0.12s ease',
            '&:hover, &:focus-visible': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.brand['600-6%'],
            },
          }}
        >
          <Typography
            variant="body3"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {question}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
